// The player app's half of the shared library's caught-error contract (see
// magical-trails-react's reportCaughtError): the library dispatches an
// `mt:caught-error` CustomEvent for every failure a boundary or degrade path
// CATCHES — failures that never reach window.onerror and were therefore
// invisible to PostHog's exception autocapture. This single listener forwards
// them:
//
//  - real Errors → posthog.captureException, tagged handled:true, so they
//    land in Error Tracking alongside the uncaught ones;
//  - degrade/context signals without an Error (AR fell back to the gradient,
//    a WebGL context was lost) → a `clientDiagnostic` event, queryable next
//    to the trail-progress events.
//
// Everything goes through posthog.capture()'s existing consent gate: an
// opted-out player sends nothing. Import once from src/index.tsx, after
// posthog.init.
import posthog from 'posthog-js';
import {
  CAUGHT_ERROR_EVENT,
  type CaughtErrorDetail,
} from 'magical-trails-react';
import { getBreadcrumbTrail } from './observability/breadcrumbs';
import { capturePlayerEvent } from './observability/outbox';

export function installCaughtErrorForwarding(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener(CAUGHT_ERROR_EVENT, (event) => {
    try {
      const detail = (event as CustomEvent<CaughtErrorDetail>).detail;
      if (!detail) return;
      const { source, kind, error, message } = detail;
      const breadcrumbs = getBreadcrumbTrail();
      if (error instanceof Error && navigator.onLine !== false) {
        posthog.captureException(error, {
          source,
          kind,
          handled: true,
          context: message,
          breadcrumbs,
        });
      } else if (error instanceof Error) {
        // Offline: PostHog's $exception shape doesn't survive a manual
        // rebuild well, so queue the essentials as a diagnostic instead of
        // losing the error with the tab.
        capturePlayerEvent('clientDiagnostic', {
          source,
          kind,
          context: message,
          thrown: error.message,
          stack: error.stack?.slice(0, 2000),
          breadcrumbs,
        });
      } else {
        capturePlayerEvent('clientDiagnostic', {
          source,
          kind,
          context: message,
          // Non-Error throw values (strings, objects) still carry signal.
          thrown: error === undefined ? undefined : String(error),
          breadcrumbs,
        });
      }
    } catch {
      // Monitoring must never break the app.
    }
  });
}
